import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, ChevronDown, ChevronUp } from 'lucide-react';
import PatientLayout from '@/components/PatientLayout';
import SEO from '@/components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const PatientFAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [filteredFaqs, setFilteredFaqs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFaqs();
  }, []);

  useEffect(() => {
    const filtered = faqs.filter(faq =>
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredFaqs(filtered);
  }, [searchTerm, faqs]);

  const fetchFaqs = async () => {
    try {
      const response = await fetch(`${API}/api/patient-faq`);
      if (response.ok) {
        const data = await response.json();
        setFaqs(data);
        setFilteredFaqs(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFaq = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (loading) {
    return (
      <PatientLayout>
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1e3a5f]"></div>
        </div>
      </PatientLayout>
    );
  }

  return (
    <PatientLayout>
      <SEO
        title="FAQ - Espace Patients"
        description="Questions frequentes sur les lentilles de contact : entretien, adaptation, problemes courants. Réponses de nos ophtalmologistes spécialistes."
        canonical="https://sfo-alc.fr/patients/faq"
      />
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 py-3">
          <Link to="/patients" className="inline-flex items-center gap-2 text-[#35AFE4] hover:text-[#1e3a5f] text-sm transition-colors" data-testid="back-to-patients">
            <ArrowLeft size={16} />
            <span>Retour à l'espace patients</span>
          </Link>
        </div>
      </div>

      {/* Header */}
      <div
        className="text-white py-14 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d6187 50%, #35AFE4 100%)' }}
      >
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="faq-title">
            Foire aux Questions
          </h1>
          <p className="text-white/85 text-lg">Retrouvez les réponses à vos questions les plus fréquentes</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" preserveAspectRatio="none" className="w-full h-12 block">
            <path fill="#f9fafb" d="M1000,4.3V0H0v4.3C0.9,23.1,126.7,99.2,500,100S1000,22.7,1000,4.3z" style={{ transform: 'rotate(180deg)', transformOrigin: 'center' }} />
          </svg>
        </div>
      </div>

      {/* Content */}
      <div className="bg-gray-50 py-10">
        <div className="max-w-4xl mx-auto px-6">
          {/* Search */}
          <div className="mb-8 max-w-lg">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher dans la FAQ..."
                className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#35AFE4]/30 focus:border-[#35AFE4] text-sm bg-white"
                data-testid="faq-search-input"
              />
            </div>
          </div>

          {/* FAQs */}
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500">
                {searchTerm ? 'Aucune question trouvée' : 'Aucune question disponible'}
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="faq-list">
              {filteredFaqs.map((faq) => (
                <div
                  key={faq.id}
                  className={`bg-white rounded-xl shadow-sm overflow-hidden border transition-all ${
                    expandedId === faq.id ? 'border-[#35AFE4] shadow-md' : 'border-gray-100'
                  }`}
                  data-testid={`faq-item-${faq.id}`}
                >
                  <button
                    onClick={() => toggleFaq(faq.id)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-base font-bold text-gray-900 pr-4" style={{ fontFamily: "'Barlow', sans-serif" }}>
                      {faq.question}
                    </h3>
                    {expandedId === faq.id ? (
                      <ChevronUp className="text-[#35AFE4] flex-shrink-0" size={20} />
                    ) : (
                      <ChevronDown className="text-gray-400 flex-shrink-0" size={20} />
                    )}
                  </button>
                  {expandedId === faq.id && (
                    <div className="px-6 pb-5">
                      <div className="pt-3 border-t border-gray-100">
                        <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{faq.answer}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Contact CTA */}
          <div className="mt-10 rounded-xl p-8 text-white text-center" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d6187 50%, #35AFE4 100%)' }}>
            <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>
              Vous n'avez pas trouvé votre réponse ?
            </h2>
            <p className="mb-5 text-white/85">Notre équipe est là pour vous aider</p>
            <Link
              to="/contact"
              className="inline-block px-7 py-3 bg-white text-[#1e3a5f] rounded-lg font-bold text-sm hover:bg-gray-100 transition-colors"
              data-testid="contact-cta-btn"
            >
              Nous contacter
            </Link>
          </div>
        </div>
      </div>
    </PatientLayout>
  );
};

export default PatientFAQPage;
